import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../interceptors";
import FeatureTree from "../feature-tree";
import { baseUrl } from "../../config/config";
import { getAllRoles } from "../../redux/actions/role-actions/role-actions";
import { setTableData } from "../../redux/slices/role-slice";
import AmpleHireLoader from "../../components/loaders/ample-hire-loader";
import ErrorToast from "../../components/toasts/error-toast";

const GET_ROLE_FEATURES_ENDPOINT = `${baseUrl}/common/base/features/features`;

const PermissionsManager = () => {
  const dispatch = useDispatch();
  const { tableData: roles, isLoading } = useSelector(
    (state) => state.roleSliceReducer
  );

  const [treeData, setTreeData] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [loadingTree, setLoadingTree] = useState(false);

  // Convert flat node list into nested tree
  const convertData = (roleFeatureNodes = []) => {
    const constructTree = (nodes, parentId = "") => {
      const newNodes = [];
      nodes.forEach((node) => {
        if (
          (parentId === "" && !node.id.includes(".")) ||
          (node.id.startsWith(parentId) &&
            node.id.split(".").length === parentId.split(".").length + 1)
        ) {
          newNodes.push({
            ...node,
            children: constructTree(nodes, node.id),
          });
        }
      });
      return newNodes;
    };
    return constructTree(roleFeatureNodes).filter(
      (n) => !n.id.includes(".")
    );
  };

  const fetchTree = async () => {
    setLoadingTree(true);
    try {
      const { data } = await axiosInstance.post(GET_ROLE_FEATURES_ENDPOINT);
      if (data.success) {
        setTreeData(convertData(data.list));
      }
    } catch (error) {
      ErrorToast(error.message || "Failed to load permissions tree");
    } finally {
      setLoadingTree(false);
    }
  };

  useEffect(() => {
    document.title = "Permissions";
    dispatch(
      getAllRoles({
        currentPage: 1,
        showRows: 100,
        filterArray: [],
        customSortArray: [],
      })
    );
    fetchTree();
  }, []);

  useEffect(() => {
    if (roles && roles.length > 0 && !selectedRoleId) {
      setSelectedRoleId(roles[0].id);
    }
  }, [roles]);

  const selectedRole = roles?.find((r) => r.id === selectedRoleId);
  const preChecked = selectedRole?.featureSetList || [];

  return (
    <div className="ah-page">
      <div className="ah-page-header">
        <h2 className="ah-page-title">Permissions</h2>
        <p className="ah-page-sub">
          Select a role and toggle which features its members can access.
        </p>
      </div>

      <div className="ah-card">
        <div className="ah-perm-toolbar">
          <div className="ah-field">
            <label>Role</label>
            <select
              className="ah-select"
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              disabled={!roles || roles.length === 0}
            >
              {(!roles || roles.length === 0) && (
                <option value="">No roles available</option>
              )}
              {roles &&
                roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
            </select>
          </div>
          {selectedRole && (
            <div className="ah-perm-meta">
              <div className="ah-pref-label">Description</div>
              <div className="ah-perm-desc">
                {selectedRole.description || "—"}
              </div>
            </div>
          )}
        </div>

        <div className="ah-perm-tree">
          {(isLoading || loadingTree) && <AmpleHireLoader />}
          {!loadingTree && treeData.length === 0 && (
            <div className="ah-empty">No permissions tree available.</div>
          )}
          {!loadingTree && treeData.length > 0 && selectedRoleId && (
            <FeatureTree
              key={selectedRoleId}
              preTreeData={treeData}
              preCheckedNodes={preChecked}
              roleId={selectedRoleId}
              setTableData={setTableData}
              tableData={roles}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsManager;
